// Détection automatique de jeux de données chiffrés dans les extractions IA
// pour proposer des visualisations à la Power BI.

export type ChartSuggestion = {
  id: string;
  /** Catégorie sémantique trouvée. */
  category: 'surfaces' | 'quantities' | 'costs' | 'schedule' | 'counts' | 'comparison' | 'metrics';
  /** Chart Kind compatible avec ChartRenderer. */
  kind: 'donut' | 'bar' | 'line' | 'radar' | 'sankey' | 'timeline' | 'table';
  title: string;
  description: string;
  source: string; // titre du document d'origine
  data: any[]; // déjà au format attendu par ChartRenderer
  xKey?: string;
  yKey?: string;
  /** Séries multiples détectées (ex: prévu/réel, brute/nette). */
  series?: Array<{ key: string; label: string; color?: string }>;
  // Pour timeline/table : structure spécifique
  timeline_items?: Array<{ date: string; title: string; text?: string }>;
  table_columns?: string[];
  table_rows?: string[][];
  unit?: string;
  stacked?: boolean;
  horizontal?: boolean;
};

function safeArray(v: any): any[] {
  return Array.isArray(v) ? v : [];
}

const CATEGORY_ALIASES: Record<string, ChartSuggestion['category']> = {
  surfaces: 'surfaces',
  surface: 'surfaces',
  quantities: 'quantities',
  quantity: 'quantities',
  counts: 'counts',
  count: 'counts',
  costs: 'costs',
  cost: 'costs',
  budget: 'costs',
  schedule: 'schedule',
  planning: 'schedule',
  metrics: 'metrics',
  bim: 'metrics',
  kpi: 'metrics',
  issues: 'comparison',
  problems: 'comparison',
  comparison: 'comparison',
  other: 'comparison',
};

function normalizeCategory(c: any): ChartSuggestion['category'] {
  const k = String(c ?? '').toLowerCase().trim();
  return CATEGORY_ALIASES[k] ?? 'comparison';
}

function humanizeKey(k: string): string {
  const map: Record<string, string> = {
    prevu: 'Prévu',
    prévu: 'Prévu',
    reel: 'Réel',
    réel: 'Réel',
    objectif: 'Objectif',
    cible: 'Cible',
    ecart: 'Écart',
    écart: 'Écart',
    brut: 'Brut',
    brute: 'Brute',
    net: 'Net',
    nette: 'Nette',
    budget: 'Budget',
    depense: 'Dépense',
    dépense: 'Dépense',
    actual: 'Réel',
    planned: 'Prévu',
  };
  const low = k.toLowerCase();
  if (map[low]) return map[low];
  return k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' ');
}

function isNumeric(v: any): boolean {
  if (typeof v === 'number' && Number.isFinite(v)) return true;
  if (typeof v === 'string') {
    const cleaned = v.replace(/[^\d.,-]/g, '').replace(',', '.');
    const n = Number(cleaned);
    return !Number.isNaN(n) && Number.isFinite(n);
  }
  return false;
}

function toNum(v: any): number {
  if (typeof v === 'number') return v;
  const cleaned = String(v).replace(/[^\d.,-]/g, '').replace(',', '.');
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

/** Choix donut vs bar selon nombre d'items et valeurs négatives. */
function pickComparisonKind(items: Array<{ label: string; value: number }>): 'donut' | 'bar' {
  if (items.some((it) => (Number(it.value) || 0) < 0)) return 'bar'; // donut ne gère pas les négatifs
  if (items.length <= 5) return 'donut';
  return 'bar';
}

/** Mots-clés pour regrouper des items paire par paire (label contient un suffixe distinctif). */
const PAIR_SUFFIXES: Array<{ re: RegExp; key: string; label: string }> = [
  { re: /\b(brut|brute|gross)\b/i, key: 'brute', label: 'Brute' },
  { re: /\b(nette?|net)\b/i, key: 'nette', label: 'Nette' },
  { re: /\b(pr[ée]vu|planifi[ée]|budget)\b/i, key: 'prevu', label: 'Prévu' },
  { re: /\b(r[ée]el|r[ée]alis[ée]|actual)\b/i, key: 'reel', label: 'Réel' },
  { re: /\b(objectif|cible|target)\b/i, key: 'objectif', label: 'Objectif' },
];

/**
 * Tente de détecter des items appariés (ex: "Logement 1 brute" + "Logement 1 nette")
 * et les regroupe en lignes multi-séries. Renvoie la liste groupée + les clés de séries détectées.
 */
function detectPairedItems(items: Array<{ label: string; value: number }>):
  | { rows: any[]; series: Array<{ key: string; label: string }> }
  | null {
  // Détecte tous les suffixes présents dans les labels
  const hits = PAIR_SUFFIXES.filter((ps) => items.some((it) => ps.re.test(it.label)));
  if (hits.length < 2) return null;

  // Normalise : extrait la "racine" du label en retirant le suffixe détecté
  const grouped: Record<string, Record<string, number>> = {};
  const rootOrder: string[] = [];
  for (const it of items) {
    const match = hits.find((h) => h.re.test(it.label));
    if (!match) continue;
    const root = it.label
      .replace(match.re, '')
      .replace(/\s{2,}/g, ' ')
      .replace(/[–—\-:()]+/g, ' ')
      .trim();
    if (!root) continue;
    if (!grouped[root]) {
      grouped[root] = {};
      rootOrder.push(root);
    }
    grouped[root][match.key] = it.value;
  }

  // Chaque racine doit avoir >=2 valeurs détectées pour que ça vaille le coup
  const valid = rootOrder.filter((root) => Object.keys(grouped[root]).length >= 2);
  if (valid.length < 2) return null;

  const rows = valid.map((root) => ({ label: root, ...grouped[root] }));
  const series = hits.map((h) => ({ key: h.key, label: h.label }));
  return { rows, series };
}

/**
 * Analyse un tableau d'extractions (issus de handleAnalyzeDoc) et propose des visualisations.
 */
export function suggestCharts(extractions: any[]): ChartSuggestion[] {
  const out: ChartSuggestion[] = [];
  if (!Array.isArray(extractions)) return out;

  let counter = 0;
  const newId = (cat: string) => `${cat}-${++counter}`;

  for (const ex of extractions) {
    if (!ex || typeof ex !== 'object') continue;
    const docTitle = String(ex.document_title ?? ex.title ?? 'Document');

    // 1) datasets explicites (nouveau schéma)
    for (const ds of safeArray(ex.datasets)) {
      const rawItems = safeArray(ds?.items).filter((it: any) => it);
      if (rawItems.length < 2) continue;

      const cat = normalizeCategory(ds?.category);
      const name = String(ds?.name ?? 'Jeu de données');
      const unit = (ds?.unit as string) ?? (rawItems[0]?.unit as string) ?? undefined;

      // Nouveau schéma : items avec "values" (objet clé→nombre) → multi-séries direct.
      const multiSeriesRows = rawItems
        .filter((it: any) => it?.values && typeof it.values === 'object')
        .map((it: any) => {
          const row: any = { label: String(it.label ?? '—') };
          for (const [k, v] of Object.entries(it.values)) row[k] = toNum(v);
          return row;
        });
      if (multiSeriesRows.length >= 2) {
        const seriesKeys = new Set<string>();
        multiSeriesRows.forEach((r) => Object.keys(r).forEach((k) => k !== 'label' && seriesKeys.add(k)));
        const series = Array.from(seriesKeys).map((k) => ({ key: k, label: humanizeKey(k) }));
        out.push({
          id: newId(cat),
          category: cat,
          kind: 'bar',
          title: name,
          description: `${multiSeriesRows.length} × ${series.length} séries — "${docTitle}"`,
          source: docTitle,
          data: multiSeriesRows,
          xKey: 'label',
          series,
          unit,
        });
        continue;
      }

      const items = rawItems
        .filter((it: any) => isNumeric(it.value))
        .map((it: any) => ({
          label: String(it.label ?? '—'),
          value: toNum(it.value),
        }));
      if (items.length < 2) continue;

      // Détection de paires sur les labels (fallback quand l'IA n'a pas utilisé "values")
      const paired = detectPairedItems(items);
      if (paired && paired.rows.length >= 2) {
        out.push({
          id: newId(cat),
          category: cat,
          kind: 'bar',
          title: name,
          description: `${paired.rows.length} × ${paired.series.length} séries — comparaison "${docTitle}"`,
          source: docTitle,
          data: paired.rows,
          xKey: 'label',
          series: paired.series,
          unit,
        });
        continue;
      }

      // Chart "nature" = donut/bar selon nombre d'items + négatifs
      const valueLabel = String(ds?.value_label ?? 'Valeur');
      const primaryKind = pickComparisonKind(items);

      out.push({
        id: newId(cat),
        category: cat,
        kind: primaryKind,
        title: name,
        description: `${items.length} valeurs détectées dans "${docTitle}"`,
        source: docTitle,
        data: items,
        xKey: 'label',
        yKey: 'value',
        unit,
      });

      // Variante alternative seulement si pas de négatifs (donut KO sur négatifs)
      if (!items.some((it) => it.value < 0)) {
        const altKind: 'donut' | 'bar' = primaryKind === 'donut' ? 'bar' : 'donut';
        out.push({
          id: newId(cat),
          category: cat,
          kind: altKind,
          title: `${name} (alt. ${altKind === 'donut' ? 'camembert' : 'barres'})`,
          description: `Variante ${altKind === 'donut' ? 'camembert' : 'barres'} — ${valueLabel}`,
          source: docTitle,
          data: items,
          xKey: 'label',
          yKey: 'value',
          unit,
        });
      }
    }

    // 2) quantities : chiffres isolés → table
    const quantities = safeArray(ex.quantities)
      .filter((q: any) => q && (q.label || q.value))
      .map((q: any) => ({
        label: String(q.label ?? '—'),
        value: q.value != null ? String(q.value) : '',
        unit: q.unit != null ? String(q.unit) : '',
      }));
    if (quantities.length >= 3) {
      out.push({
        id: newId('quantities'),
        category: 'quantities',
        kind: 'table',
        title: 'Tableau récapitulatif des quantités',
        description: `${quantities.length} indicateurs chiffrés extraits de "${docTitle}"`,
        source: docTitle,
        data: [],
        table_columns: ['Indicateur', 'Valeur', 'Unité'],
        table_rows: quantities.map((q) => [q.label, q.value, q.unit]),
      });
    }

    // 3) schedule → timeline
    const schedule = safeArray(ex.schedule)
      .filter((s: any) => s && (s.phase || s.date))
      .map((s: any) => ({
        date: String(s.date ?? '—'),
        title: String(s.phase ?? s.title ?? 'Phase'),
        text: s.duration_months ? `Durée : ${s.duration_months} mois` : undefined,
      }));
    if (schedule.length >= 2) {
      out.push({
        id: newId('schedule'),
        category: 'schedule',
        kind: 'timeline',
        title: 'Planning du projet',
        description: `${schedule.length} jalons détectés dans "${docTitle}"`,
        source: docTitle,
        data: [],
        timeline_items: schedule,
      });
    }

    // 4) materials/technical_systems → comparaison count si multiples
    const materials = safeArray(ex.materials).filter((m: any) => typeof m === 'string');
    const systems = safeArray(ex.technical_systems).filter((m: any) => typeof m === 'string');
    if (materials.length >= 3 || systems.length >= 3) {
      const items: Array<{ label: string; value: number }> = [];
      if (materials.length > 0) items.push({ label: 'Matériaux', value: materials.length });
      if (systems.length > 0) items.push({ label: 'Systèmes techniques', value: systems.length });
      if (items.length >= 2) {
        out.push({
          id: newId('counts'),
          category: 'counts',
          kind: 'donut',
          title: 'Volume de catégories techniques',
          description: "Nombre d'éléments par catégorie",
          source: docTitle,
          data: items,
          xKey: 'label',
          yKey: 'value',
        });
      }
    }
  }

  return out;
}

/** Convertit une suggestion en Section pour l'ajouter à la présentation. */
export function suggestionToSection(s: ChartSuggestion): any {
  const id = `sec_${s.id}_${Date.now()}`;
  if (s.kind === 'timeline') {
    return {
      id,
      type: 'timeline',
      title: s.title,
      timeline: { items: s.timeline_items ?? [] },
    };
  }
  if (s.kind === 'table') {
    return {
      id,
      type: 'table',
      title: s.title,
      table: { columns: s.table_columns ?? [], rows: s.table_rows ?? [] },
    };
  }
  return {
    id,
    type: 'chart',
    title: s.title,
    chart: {
      kind: s.kind,
      data: s.data,
      xKey: s.xKey,
      yKey: s.yKey,
      label: s.unit ? `${s.unit}` : s.title,
      series: s.series,
      unit: s.unit,
      stacked: s.stacked,
      horizontal: s.horizontal,
    },
  };
}
